import Microgame from '../engine/Microgame.js';

export default class SortGame extends Microgame {

    static getPlayback() {
        return {
            title: { fr: `Trie !`, en: `Sort!`, es: `¡Clasifica!` },
            controls: { fr: `Glisse les objets vers la bonne poubelle ou catégorie`, en: `Swipe objects into the correct bin or category`, es: `Desliza los objetos hacia la papelera o categoría correcta` },
            objective: { fr: `Range chaque chose à sa place rapidement`, en: `Put everything in its place quickly`, es: `Pon todo en su lugar rápidamente` },
            icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="var(--primary-color)" stroke-width="2"><path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M10 11v6M14 11v6"/></svg>`,
            actions: [
                { type: 'move', x: 50, y: 50, duration: 260 },
                { type: 'swipe', fromX: 50, fromY: 50, toX: 82, toY: 50, duration: 340 },
                { type: 'wait', duration: 320 },
                { type: 'swipe', fromX: 50, fromY: 50, toX: 18, toY: 50, duration: 340 },
                { type: 'wait', duration: 420 },
            ],
            loopDuration: 3000
        };
    }
    constructor(engine) {
        super(engine);
        this.items = [];
        this.currentItem = null;
        this.score = 0;
        this.targetScore = (this.engine.activeChallenge && this.engine.activeChallenge.config && this.engine.activeChallenge.config.targetScore) || 1;
        this.startX = 0;
        this.currentX = 0;
        this.isDragging = false;
    }

    setup() {
        this.engine.ui.instruction.textContent = "SORT!";

        // Zones
        const leftZone = document.createElement('div');
        leftZone.style.position = 'absolute';
        leftZone.style.left = '0';
        leftZone.style.top = '0';
        leftZone.style.width = '100px';
        leftZone.style.height = '100%';
        leftZone.style.background = `linear-gradient(90deg, ${this.primaryColor}4D, transparent)`; // 4D is ~30% opacity
        leftZone.style.borderLeft = `5px solid ${this.primaryColor}`;
        leftZone.style.pointerEvents = 'none';
        this.container.appendChild(leftZone);

        const rightZone = document.createElement('div');
        rightZone.style.position = 'absolute';
        rightZone.style.right = '0';
        rightZone.style.top = '0';
        rightZone.style.width = '100px';
        rightZone.style.height = '100%';
        rightZone.style.background = `linear-gradient(-90deg, ${this.secondaryColor}4D, transparent)`;
        rightZone.style.borderRight = `5px solid ${this.secondaryColor}`;
        rightZone.style.pointerEvents = 'none';
        this.container.appendChild(rightZone);

        this.spawnItem();

        this.onDown = this.onDown.bind(this);
        this.onMove = this.onMove.bind(this);
        this.onUp = this.onUp.bind(this);

        this.engine.input.element.addEventListener('pointerdown', this.onDown);
        this.engine.input.element.addEventListener('pointermove', this.onMove);
        this.engine.input.element.addEventListener('pointerup', this.onUp);
    }

    spawnItem() {
        if (this.currentItem) this.currentItem.remove();

        const isLeft = Math.random() > 0.5;
        this.currentItem = document.createElement('div');
        this.currentItem.style.position = 'absolute';
        this.currentItem.style.top = '50%';
        this.currentItem.style.left = '50%';
        this.currentItem.style.transform = 'translate(-50%, -50%)';
        this.currentItem.style.width = '150px';
        this.currentItem.style.height = '200px';
        this.currentItem.style.borderRadius = '15px';

        const color = isLeft ? this.primaryColor : this.secondaryColor;
        this.currentItem.style.background = color;
        this.currentItem.style.boxShadow = `0 0 30px ${color}`;

        this.currentItem.style.display = 'flex';
        this.currentItem.style.justifyContent = 'center';
        this.currentItem.style.alignItems = 'center';
        this.currentItem.style.fontSize = '3rem';
        this.currentItem.textContent = isLeft ? 'LEFT' : 'RIGHT';
        this.currentItem.dataset.side = isLeft ? 'left' : 'right';
        this.container.appendChild(this.currentItem);

        this.currentX = 0;
    }

    onDown(e) {
        if (!this.currentItem || !this.active) return;
        this.isDragging = true;
        this.startX = e.clientX;
        this.currentItem.style.transition = 'none';
    }

    onMove(e) {
        if (!this.isDragging || !this.currentItem) return;
        const dx = e.clientX - this.startX;
        this.currentX = dx;

        // Rotate slightly while dragging
        const rotate = dx * 0.1;
        this.currentItem.style.transform = `translate(calc(-50% + ${dx}px), -50%) rotate(${rotate}deg)`;
    }

    onUp(e) {
        if (!this.isDragging || !this.currentItem) return;
        this.isDragging = false;

        const threshold = 100;
        if (Math.abs(this.currentX) > threshold) {
            // Swiped far enough
            const dir = this.currentX > 0 ? 'right' : 'left';
            const side = this.currentItem.dataset.side;

            let correct = false;
            if (dir === side) correct = true;

            if (correct) {
                // Fly away
                const endX = dir === 'left' ? -500 : 500;
                this.currentItem.style.transition = 'transform 0.3s ease-out';
                this.currentItem.style.transform = `translate(calc(-50% + ${endX}px), -50%) rotate(${this.currentX * 0.2}deg)`;

                this.score++;

                // Check if target score reached (STANDARD mode: 1, challenge: config)
                if (this.score >= this.targetScore) {
                    this.win();
                    return;
                }

                // Show score update (challenge only)
                if (this.engine.activeChallenge && (this.engine.activeChallenge.mode === 'infinite' || this.engine.activeChallenge.config.targetScore)) {
                    this.engine.score = this.score;
                    this.engine.updateHUD();
                }

                // Keep sorting - spawn next item
                setTimeout(() => this.spawnItem(), 300);
            } else {
                if (this.engine.activeChallenge) {
                    this.engine.score = this.score; // Ensure final score is recorded
                }
                this.lose();
            }
        } else {
            // Snap back
            this.currentItem.style.transition = 'transform 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)';
            this.currentItem.style.transform = 'translate(-50%, -50%)';
        }
    }

    onTimeout() {
        // In challenge mode, timeout = end of run (success/score recorded)
        if (this.engine.activeChallenge) return true;
        // In STANDARD mode, timeout = fail
        return false;
    }

    cleanup() {
        super.cleanup();
        this.engine.input.element.removeEventListener('pointerdown', this.onDown);
        this.engine.input.element.removeEventListener('pointermove', this.onMove);
        this.engine.input.element.removeEventListener('pointerup', this.onUp);
    }
}
